/**
 * Pure helpers for the unified cross-batch PRESS station (B3). Regroups placements — screen-print AND
 * DTF — into press runs (one screen / one heat-press setup) across batches. Kept separate from the
 * component so the regrouping is unit-tested without a DOM.
 */
import type {
  PressMethod,
  PressWorkItem,
  PressWorklistBatch,
  PressWorklistResponse,
} from '@fsp/contracts';
import { pullBlankLabel, pullBlankVariant, sizeRank } from './inventory-lib';

/** One placement in a press run, tagged with the batch it came from so check-off targets it. */
export type PressRunItem = PressWorkItem & { batchId: string; batchSeq?: number };

/** Every garment that takes one press setup (a technique + design at a location), across all batches. */
export type PressRun = {
  /** Stable group key: technique + design identity + location. */
  key: string;
  /** Which technique this run is pressed with — screen-print and DTF never share a setup. */
  method: PressMethod;
  /** The setup the operator mounts — the design's name (or short id / "Unresolved art"). */
  designLabel: string;
  /** Where on the garment it prints (front / back / left-chest …). */
  location: string;
  items: PressRunItem[];
  /** Shirts in this run — Σ quantity across every placement. */
  totalGarments: number;
  /** Shirts already pressed — Σ quantity of the placements marked pressed. */
  pressedGarments: number;
};

/** Group identity for a design: prefer the design id, fall back to its name, else a shared null bucket. */
function designKeyOf(i: PressWorkItem): string {
  if (i.designId) return `id:${i.designId}`;
  if (i.designName) return `name:${i.designName}`;
  return 'none';
}

/** Human label for the setup: design name, else a short id, else a flag that art is unresolved. */
export function designLabelOf(i: PressWorkItem): string {
  if (i.designName) return i.designName;
  if (i.designId) return i.designId.slice(0, 8);
  return 'Unresolved art';
}

/** Human label for a press technique. */
export function methodLabelOf(method: PressMethod): string {
  return method === 'dtf' ? 'DTF' : 'Screen print';
}

// ── Per-copy press state (DTF) ──────────────────────────────────────────────────────────────────
// A DTF placement can be checked off copy-by-copy. The scalar `pressedAt` is the "all owed copies
// pressed" flag (authoritative when present); while it is absent, `pressedCopies` is the partial set.
// Screen-print stays coarse (one tick for the whole run) — these treat SP items as whole-placement.

type CopyState = Pick<PressWorkItem, 'pressedAt' | 'quantity' | 'pressedCopies'>;

/** The 0-based copy indexes shown pressed: a set `pressedAt` means "all copies", else the explicit set. */
export function pressedCopiesOf(item: CopyState): number[] {
  if (item.pressedAt) return Array.from({ length: item.quantity }, (_, k) => k);
  return (item.pressedCopies ?? []).filter((k) => k >= 0 && k < item.quantity);
}

/** How many copies of this placement are pressed — for the run/pull rollups and the "k / N" label. */
export function pressedCopyCount(item: CopyState): number {
  return item.pressedAt ? item.quantity : pressedCopiesOf(item).length;
}

/** Whether the press station shows this placement as per-copy checkboxes (DTF with more than one copy)
 *  rather than a single whole-placement checkbox. Screen-print + single-copy stay one checkbox. */
export function isPerCopy(item: Pick<PressWorkItem, 'method' | 'quantity'>): boolean {
  return item.method === 'dtf' && item.quantity > 1;
}

/** Human batch label — "Batch #12" by its human seq, else a short id. */
export function batchLabelOf(batch: Pick<PressWorklistBatch, 'seq' | 'batchId'>): string {
  return batch.seq ? `Batch #${batch.seq}` : `Batch ${batch.batchId.slice(0, 8)}`;
}

/** How the press station narrows its worklist before grouping into runs. */
export type WorklistFilter = {
  /** Batch ids to keep — the batches the operator wants to queue. EMPTY set = keep every batch. */
  selectedBatchIds: ReadonlySet<string>;
  /** Keep one technique, or 'all'. */
  methodFilter: 'all' | PressMethod;
};

/**
 * Narrow the worklist to the batches the operator wants to queue and (optionally) one technique.
 * Batch selection runs first (empty set = every batch), then the technique filter — a batch left with
 * no matching placement drops out (mirroring the old inline method filter). Pure so the station's
 * filters are unit-tested without a DOM. Selecting a set of batches also narrows the derived press
 * runs, since `groupByPressRun` is fed this result.
 */
export function filterWorklistBatches(
  batches: readonly PressWorklistBatch[],
  { selectedBatchIds, methodFilter }: WorklistFilter,
): PressWorklistBatch[] {
  let out =
    selectedBatchIds.size > 0
      ? batches.filter((b) => selectedBatchIds.has(b.batchId))
      : [...batches];
  if (methodFilter !== 'all') {
    out = out
      .map((b) => ({ ...b, items: b.items.filter((i) => i.method === methodFilter) }))
      .filter((b) => b.items.length > 0);
  }
  return out;
}

/**
 * Regroup the batch-grouped worklist into PRESS RUNS — one group per (technique + design + location)
 * across every batch — mirroring how a press is actually set up: mount one screen / load one design,
 * then run every garment that takes it, whatever batch it came from. The default view is grouped by
 * batch, which scatters a single setup's garments across separate cards.
 *
 * Technique is part of the key so a design pressed screen-print-front in one batch and DTF-front in
 * another never collapse into one run — you don't press screen print and DTF on the same setup. Each
 * item keeps its `batchId` so the existing per-placement check-off still marks the right batch. Runs
 * are sorted by location then design; within a run, an order's garments sit together.
 */
export function groupByPressRun(batches: readonly PressWorklistBatch[]): PressRun[] {
  const runs = new Map<string, PressRun>();
  for (const b of batches) {
    for (const i of b.items) {
      const key = `${i.method}##${designKeyOf(i)}##${i.location}`;
      let run = runs.get(key);
      if (!run) {
        run = {
          key,
          method: i.method,
          designLabel: designLabelOf(i),
          location: i.location,
          items: [],
          totalGarments: 0,
          pressedGarments: 0,
        };
        runs.set(key, run);
      }
      run.items.push({ ...i, batchId: b.batchId, batchSeq: b.seq });
      run.totalGarments += i.quantity;
      // Count pressed COPIES, not whole placements, so a partially-pressed DTF row's progress shows.
      run.pressedGarments += pressedCopyCount(i);
    }
  }
  for (const run of runs.values()) {
    run.items.sort(
      (a, z) =>
        a.orderName.localeCompare(z.orderName) ||
        a.title.localeCompare(z.title) ||
        (a.variantTitle ?? '').localeCompare(z.variantTitle ?? ''),
    );
  }
  return [...runs.values()].sort(
    (a, z) => a.location.localeCompare(z.location) || a.designLabel.localeCompare(z.designLabel),
  );
}

// ── Blanks-to-pull rollup ────────────────────────────────────────────────────────────────────────

/** S&S identity + names for a blank (the `GET /inventory` `BlankOnHand` subset the labels need). */
export type BlankName = {
  ssSku: string;
  brandName?: string;
  styleName?: string;
  colorName?: string;
  sizeName?: string;
};

/** One line of a "blanks to pull" list — a physical blank (style+color+size) and how many to grab. */
export type BlankPullRow = {
  /** Group key: the physical blank's ssSku when resolved, else a product+variant fallback key. */
  key: string;
  /** The physical blank SKU, when the item resolved one. */
  ssSku?: string;
  /** Body label — "Tultex 602" (canonical), else the Shopify product title fallback. */
  label: string;
  /** Colour · size sub-label ("Black · XL"), when known. */
  variant: string;
  /** Raw size (canonical `sizeName`, else the Shopify variant) — kept for display + ordering. */
  sizeName: string;
  /** Garments to pull for this blank in the run (Σ placement quantity). */
  quantity: number;
  /** Distinct Shopify product titles that resolve to this blank — >1 when several collapse into one. */
  titles: string[];
};

const BLANK_PREFIX = 'BLANK#';
/** A line's `resolvedBlankSku` is `BLANK#<ssSku>`; the on-hand list keys on the bare `<ssSku>`. */
function bareSsSku(resolvedBlankSku: string): string {
  return resolvedBlankSku.startsWith(BLANK_PREFIX)
    ? resolvedBlankSku.slice(BLANK_PREFIX.length)
    : resolvedBlankSku;
}

/** Fold spelled-out sizes to the abbreviations `sizeRank` orders ("2X-Large" → "2XL", "Small" → "S"). */
function abbreviateSize(raw: string): string {
  const s = raw.trim().toUpperCase();
  const spelled: Record<string, string> = {
    'X-SMALL': 'XS',
    SMALL: 'S',
    MEDIUM: 'M',
    LARGE: 'L',
    'X-LARGE': 'XL',
  };
  if (spelled[s]) return spelled[s];
  const m = s.match(/^(\d)X-?LARGE$/); // "2X-Large" / "2XLARGE" → "2XL"
  return m ? `${m[1]}XL` : s;
}

/**
 * Roll a press run's placements up into a "blanks to pull" list — one row per physical blank
 * (`resolvedBlankSku`, which is style+color+size specific), summing garment quantity. Different Shopify
 * products that resolve to the SAME blank collapse into one row (the catalog runs on ~2 blank bodies),
 * so the operator reads a short shelf-pull list instead of the per-order rows.
 *
 * `nameBySku` (bare ssSku → names, from `GET /inventory`) supplies the human blank label; an item whose
 * blank isn't in the map — or that never resolved a blank — falls back to grouping by product title +
 * variant, labelled by the product. Rows sort by body label, then colour, then size (S → 6XL, then
 * youth/unknown last).
 *
 * Per-run by design: a garment with a front AND a back placement is in BOTH runs, so a shirt is counted
 * once per run it passes. Correct for "what to stage at this press setup"; to get physical shirts to
 * pull from the shelf, feed {@link dedupeGarments} first so each garment counts once across all runs.
 */
export function blankPullList(
  items: readonly PressWorkItem[],
  nameBySku: ReadonlyMap<string, BlankName>,
): BlankPullRow[] {
  type Internal = BlankPullRow & { colorName: string };
  const rows = new Map<string, Internal>();
  for (const it of items) {
    // A TRANSFER-ONLY reprint presses onto the shirt already out on the floor: mark-printed draws no
    // replacement blank for it, so listing one to pull walks real stock off the shelf that the ledger
    // never decrements. The batch run sheet's own pull list has the same skip — these two are siblings
    // and must agree, which is exactly why fixing only one of them was a review find.
    if (it.reissueTransferOnly) continue;
    const sku = it.resolvedBlankSku ? bareSsSku(it.resolvedBlankSku) : undefined;
    const name = sku ? nameBySku.get(sku) : undefined;
    // Collapse by the physical blank when known; else the product+variant can't be merged further.
    const key = sku ?? `t:${it.title}##${it.variantTitle ?? ''}`;
    let row = rows.get(key);
    if (!row) {
      row = {
        key,
        ssSku: sku,
        // A resolved-but-nameless inventory row would otherwise label the row with the bare sku; the
        // helper falls back to the item's readable Shopify title/variant in that case.
        label: pullBlankLabel(name, it.title),
        variant: pullBlankVariant(name, it.variantTitle),
        sizeName: name?.sizeName ?? it.variantTitle ?? '',
        colorName: name?.colorName ?? '',
        quantity: 0,
        titles: [],
      };
      rows.set(key, row);
    }
    row.quantity += it.quantity;
    if (!row.titles.includes(it.title)) row.titles.push(it.title);
  }
  const sorted = [...rows.values()].sort(
    (a, z) =>
      a.label.localeCompare(z.label) ||
      a.colorName.localeCompare(z.colorName) ||
      sizeRank(abbreviateSize(a.sizeName)) - sizeRank(abbreviateSize(z.sizeName)) ||
      a.sizeName.localeCompare(z.sizeName),
  );
  return sorted.map((r) => ({
    key: r.key,
    ssSku: r.ssSku,
    label: r.label,
    variant: r.variant,
    sizeName: r.sizeName,
    quantity: r.quantity,
    titles: r.titles,
  }));
}

/**
 * Collapse the runs to ONE entry per garment line PER BATCH, keeping the first seen. A garment that
 * appears in several runs of the SAME sheet — its screen-print front and its DTF back are two press
 * setups off one batch — is a single physical shirt, so `blankPullList(dedupeGarments(runs), …)` is the
 * true shelf-pull total, versus the per-run lists which count that shirt once per setup it passes.
 *
 * ⚠️ THE BATCH SEGMENT IS LOAD-BEARING, and its absence was an UNDER-pull — the one error direction that
 * strands a presser with a transfer and no shirt.
 *
 * A replacement blank is drawn PER BATCH: mark-printed calls `blankConsumables` over that sheet's items
 * alone, keyed `PRINT#store#order#line#<batchId>`, so two sheets carrying one line draw two blanks. Since
 * the reprint flush groups per (line, production RUN), two sheets for one line is now the routine outcome
 * of a single flush click, and both are READY so both reach the press worklist. Keyed without the batch,
 * this dropped the second sheet's item outright and printed ONE blank to pull for two garments.
 *
 * Worse, and order-dependent: per-run `transferOnly` means one sheet's garment can keep its blank while
 * the other needs a fresh one. `blankPullList` skips a `reissueTransferOnly` item, so whenever the
 * keep-the-blank sheet happened to be seen FIRST the rollup printed **zero** blanks for a line that
 * genuinely needed one.
 *
 * A line cannot span two sheets any other way — a build puts every placement of a line on one batch, and
 * a batch's several film sheets share its id — so adding the segment costs nothing that the premise above
 * was protecting.
 */
export function dedupeGarments(runs: readonly PressRun[]): PressWorkItem[] {
  const seen = new Set<string>();
  const out: PressWorkItem[] = [];
  for (const run of runs) {
    for (const it of run.items) {
      const k = `${it.storeId}#${it.orderId}#${it.lineId}#${it.batchId}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(it);
    }
  }
  return out;
}

// ── Optimistic press check-off ─────────────────────────────────────────────────────────────────────

/**
 * Which single placement a mark toggles, and its new pressed state. A placement is identified by ALL
 * of (method, batchId, storeId, orderId, lineId, location) — the tuple that is unique in the worklist
 * — so a garment's front vs back, or its screen-print vs DTF placement, are never confused. Also the
 * mark mutation's variables.
 */
export type OptimisticMarkTarget = {
  method: PressMethod;
  batchId: string;
  storeId: string;
  orderId: string;
  lineId: string;
  location: string;
  /** true = pressed (write `pressedAt`); false = un-pressed (clear it). */
  pressed: boolean;
  /** DTF per-copy check-off: the 0-based copy toggled. Absent = whole-placement (SP / single copy). */
  copyIndex?: number;
};

/**
 * Return a copy of a cached press worklist with the single placement `target` identifies flipped to
 * `target.pressed`. Powers the optimistic check-off — flip the checkbox the instant it's clicked,
 * before the server round-trip — and, called with `pressed` inverted, the on-error rollback of that
 * same placement. Surgical: only the matching item in the matching batch changes, so a concurrent
 * mark on a DIFFERENT batch is never clobbered by this one's rollback (a whole-cache snapshot restore
 * would be). Non-matching input is returned with its arrays rebuilt but values unchanged. Only the
 * PRESENCE of `pressedAt` drives the checkbox; the exact `pressedAtIso` value is reconciled by the
 * settle-time refetch, so any ISO string is fine here.
 */
export function applyOptimisticMark(
  data: PressWorklistResponse,
  target: OptimisticMarkTarget,
  pressedAtIso: string,
): PressWorklistResponse {
  const matches = (it: PressWorkItem) =>
    it.method === target.method &&
    it.storeId === target.storeId &&
    it.orderId === target.orderId &&
    it.lineId === target.lineId &&
    it.location === target.location;
  return {
    ...data,
    batches: data.batches.map((b) => {
      if (b.batchId !== target.batchId) return b;
      return {
        ...b,
        items: b.items.map((it) => {
          if (!matches(it)) return it;
          if (target.copyIndex === undefined) {
            // Whole-placement toggle (screen-print / single copy): flip the scalar flag.
            return { ...it, pressedAt: target.pressed ? pressedAtIso : undefined };
          }
          // Per-copy toggle: add/remove the copy, deriving the scalar from the resulting set. Uses the
          // item's `quantity` as the copy count — the exact refund-netted owed is reconciled by the
          // settle-time refetch (as with the whole-placement path), so optimism can lag by one refresh.
          const all = Array.from({ length: it.quantity }, (_, k) => k);
          const next = new Set(pressedCopiesOf(it));
          if (target.pressed) next.add(target.copyIndex);
          else next.delete(target.copyIndex);
          const complete = all.every((k) => next.has(k));
          return {
            ...it,
            // Canonical "full" shape mirrors the server: pressedAt set + pressedCopies cleared.
            pressedAt: complete ? pressedAtIso : undefined,
            pressedCopies: complete ? undefined : [...next].sort((a, z) => a - z),
          };
        }),
      };
    }),
  };
}
